import url from 'url';
import { PlayerStatus } from '@/shared/components/player/enums';

/**
 * Consrtructor for the strategy. This is what gets passed to the SDK via:
 * window['_cbv_strategies'].push(Strategy);
 * @param {Object} player A pointer to the player being tracked. The player
 * is where all the video data is derived from
 */
function Strategy(player) {
  this.state = player.state;
}

/**
 * Enum for the content type. One of these values should be returned from
 * the "getContentType" function.
 * @enum {string}
 */
Strategy.ContentType = {
  AD: 'ad',
  CONTENT: 'ct',
};

/**
 * Enum for the ad position. One of these values should be returned from
 * the "getAdPosition" function.
 * @enum {string}
 */
Strategy.AdPosition = {
  PREROLL: 'a1',
  MIDROLL: 'a2',
  POSTROLL: 'a3',
  OVERLAY: 'a4',
  SPECIAL: 'a5',
};

/**
 * Enum for the video state. One of these values should be returned from
 * the "getState" function.
 * @enum {string}
 */
Strategy.VideoState = {
  UNPLAYED: 's1',
  PLAYED: 's2',
  STOPPED: 's3',
  COMPLETED: 's4',
};

/**
 * Indicates if the video strategy is ready for pinging.
 * Typically this is called when all path and title metadata
 * is available
 * Note: Pings should only be sent after this reads true.
 * @return {boolean} The ready state of the strategy.
 */
Strategy.prototype.isReady = function () {
  return this.state.product.id;
};

/**
 * Gets the human readable video title.
 * This is returned in the i key of the ping.
 * @return {string} The video title.
 */
Strategy.prototype.getTitle = function () {
  return this.state.product.title;
};

/**
 * Gets the video path.
 * This is returned in the p key of the ping.
 * Note: this should be the playable video path if available.
 * @return {string} The video path.
 */
Strategy.prototype.getVideoPath = function () {
  const canonicalUrl = this.state.product.canonicalUrl || '';
  return url.parse(canonicalUrl).pathname || window.location.pathname;
};

/**
 * Gets the type of video playing. Returns value from the ContentType enum.
 * This is returned in the _vt key of the ping.
 * @return {Strategy.ContentType} The type of content (ad or ct).
 */
Strategy.prototype.getContentType = function () {
  return Strategy.ContentType.CONTENT;
};

/**
 * Gets the ad position. Returns value from the AdPosition enum.
 * This is returned in the _vap key of the ping.
 * @return {Strategy.AdPosition|string} The ad position
 * from a1 (pre-roll), a2 (mid-roll), a3 (post-roll),
 * a4 (overlay), or a5 (special).
 */
Strategy.prototype.getAdPosition = function () {};

/**
 * Gets the total duration of the video.
 * This is returned in the _vd key of the ping.
 * @return {number} The total duration time in milliseconds.
 */
Strategy.prototype.getTotalDuration = function () {
  return (this.state.duration || this.state.product.duration) * 1000;
};

/**
 * Gets the current state of the video. Returns value from the VideoState enum.
 * This is returned in the _vs key of the ping.
 * @return {string} The current video state. {@link Strategy.VideoState}
 */
Strategy.prototype.getState = function () {
  if ([PlayerStatus.PAUSE, PlayerStatus.ADVERTS_PAUSE, PlayerStatus.WAITING_INTERACTION].includes(this.state.playerStatus)) {
    return Strategy.VideoState.STOPPED;
  }

  if ([PlayerStatus.PLAYING, PlayerStatus.ADVERTS_PLAYING].includes(this.state.playerStatus)) {
    return Strategy.VideoState.PLAYED;
  }

  if (this.state.playerStatus === PlayerStatus.LOADING) {
    return Strategy.VideoState.UNPLAYED;
  }

  return Strategy.VideoState.COMPLETED;
};

/**
 * Gets the current play time of the video (where the playhead is).
 * This is returned in the _vpt key of the ping.
 * @return {number} The current play time in milliseconds.
 */
Strategy.prototype.getCurrentPlayTime = function () {};

/**
 * Gets the current bitrate of the video.
 * This is returned in the _vbr key of the ping.
 * @return {number} The current bitrate in kbps.
 */
Strategy.prototype.getBitrate = function () {};

/**
 * Gets the thumbnail of the video.
 * This is returned in the _vtn key of the ping.
 * @return {string} The [absolute] path to the thumbnail.
 */
Strategy.prototype.getThumbnailPath = function () {
  return this.state.product.images?.['16x9']?.[0]?.mainUrl;
};

/**
 * Gets the video player type.
 * This is returned in the _vplt key of the ping.
 * @return {string} The player type (user defined).
 */
Strategy.prototype.getPlayerType = function () {};

/**
 * Gets the time since start of viewing.
 * This is returned in the _vvs key of the ping.
 * @return {number} The time since viewing started in milliseconds.
 */
Strategy.prototype.getViewStartTime = function () {};

/**
 * Gets the time since start of viewing for users in a play state -- regardless of current state.
 * This is returned in the _vvsp key of the ping.
 * @return {number} The time since viewing started in milliseconds.
 */
Strategy.prototype.getViewPlayTime = function () {};

/**
 * Gets the time since play start for users who saw a preroll ad -- regardless of current state.
 * The timer continues after the ad completes as well.
 * This is returned in the _vasp key of the ping.
 * @return {number} The time since viewing started in milliseconds.
 */
Strategy.prototype.getViewAdPlayTime = function () {};

/**
 * Verifies that the given player belongs to this strategy. Used for a
 * greedy search of the matching strategy for a given element or object.
 * @param {Object} player A pointer to the player being tracked.
 * @return {boolean} If the strategy can handle this type of object.
 */
Strategy.verify = function (player) {
  return Object.prototype.hasOwnProperty.call(player, 'state');
};

export { Strategy };
